import React from 'react'
import { CreditCard, RefreshCw, ArrowLeft, AlertCircle } from 'lucide-react'
import ErrorBoundary from './ErrorBoundary'

interface CheckoutErrorBoundaryProps {
    children: React.ReactNode
}

const CheckoutErrorFallback: React.FC = () => {
    const handleGoToBasket = () => {
        window.location.href = '/?basket=open'
    }

    const handleReload = () => {
        window.location.reload()
    }

    const handleGoHome = () => {
        window.location.href = '/'
    }

    return (
        <div className="min-h-[500px] flex flex-col items-center justify-center p-8 bg-orange-50 rounded-lg border border-orange-200">
            <div className="bg-orange-100 rounded-full p-3 mb-4">
                <AlertCircle className="h-8 w-8 text-orange-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-orange-900 mb-2">
                Checkout Error
            </h2>
            <p className="text-orange-700 text-center mb-2 max-w-md">
                We encountered an issue processing your checkout. Don't worry - your basket is still saved.
            </p>
            <p className="text-orange-600 text-center mb-6 max-w-md text-sm">
                Please try again or contact support if the problem persists.
            </p>
            
            <div className="flex flex-col gap-3 w-full max-w-sm">
                <button
                    onClick={handleReload}
                    className="flex items-center justify-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors font-medium"
                >
                    <RefreshCw className="h-4 w-4" />
                    Try Checkout Again
                </button>
                
                <button
                    onClick={handleGoToBasket}
                    className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <CreditCard className="h-4 w-4" />
                    Review Basket
                </button>
                
                <button
                    onClick={handleGoHome}
                    className="flex items-center justify-center gap-2 bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Continue Shopping
                </button>
            </div>

            <div className="mt-6 p-4 bg-orange-100 rounded-lg border border-orange-200">
                <p className="text-xs text-orange-700 text-center">
                    <strong>Need help?</strong> Contact support with the error details below
                </p>
            </div>
        </div>
    )
}

const CheckoutErrorBoundary: React.FC<CheckoutErrorBoundaryProps> = ({ children }) => {
    return (
        <ErrorBoundary 
            fallback={<CheckoutErrorFallback />}
            onError={(error, errorInfo) => {
                // Checkout-specific error handling
                console.group('💳 Checkout Error Details')
                console.error('Checkout component error:', error)
                console.error('Error info:', errorInfo)
                console.groupEnd()

                // Critical: Report checkout errors immediately
                // This is where you'd integrate with your error tracking service
                // trackCriticalError('checkout_error', { error: error.message, stack: error.stack })
            }}
        >
            {children}
        </ErrorBoundary>
    )
}

export default CheckoutErrorBoundary